/* @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import moment from 'moment';
import _ from 'underscore';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import { FormattedMessage } from 'js/lib/coursera.react-intl';
import waitFor from 'js/lib/waitFor';

import { SelectField, SelectOption, withTheme } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

import _t from 'i18n!nls/discussions';

const dateFormat = 'MMM DD, YYYY';

type Filter = {
  id: string;
  branchId: string;
  startedAt: number;
  endedAt: number;
};

type Branch = {
  id: string;
  label: string;
  filters: Filter[];
};

type Session = {
  id: string;
  moduleDeadlines: any;
  startedAt: number;
  endedAt: number;
};

type Props = {
  handleDropdownChange: (p1: $TSFixMe, p2: $TSFixMe) => {};
  id: string;
  branches: Branch[];
  allSessions: Session[];
  selectedFilter: {
    id: string;
    startedAt: number;
    endedAt: number;
  };
  hideAllSessionsOption: boolean;
  limitSwitchingToCurrentBranch: boolean;
  currentBranchId: string;
  isSessionsV2Enabled: boolean;
  theme: Theme;
};

type State = {
  selectedFilter: Filter;
};

class SessionFilterDropdown extends React.Component<Props, State> {
  constructor(props: $TSFixMe, context: $TSFixMe) {
    super(props, context);

    if (props.hideAllSessionsOption && props.selectedFilter.id === 'all') {
      const selectedBranch =
        props.branches.find((branch: $TSFixMe) => branch.id === props.currentBranchId) || props.branches[0];
      this.state = {
        selectedFilter: selectedBranch.filters.find((filter: $TSFixMe) => filter.id !== 'all'),
      };
      props.handleDropdownChange(this.state.selectedFilter.id);
    } else {
      this.state = {
        selectedFilter: props.selectedFilter,
      };
    }
  }

  handleDropdownChange = (filterId: $TSFixMe) => {
    let selectedBranch;
    let selectedFilter;
    let filterIdWithoutBranch;
    if (filterId.startsWith('all~')) {
      selectedBranch = this.props.branches.find((branch) => branch.id === filterId.substring(4, filterId.length));
      // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
      selectedFilter = selectedBranch.filters.find((filter) => filter.id === 'all');
      filterIdWithoutBranch = 'all';
    } else {
      selectedBranch = this.props.branches.find((branch) => !!branch.filters.find((filter) => filter.id === filterId));
      // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
      selectedFilter = selectedBranch.filters.find((filter) => filter.id === filterId);
      filterIdWithoutBranch = filterId;
    }

    this.setState({
      // @ts-expect-error ts-migrate(2322) FIXME: Type 'Filter | undefined' is not assignable to typ... Remove this comment to see the full error message
      selectedFilter,
    });

    // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
    this.props.handleDropdownChange(filterIdWithoutBranch, selectedBranch.id);
  };

  buildBranchMenuItemDisplay = (branch: $TSFixMe): JSX.Element => {
    return (
      <SelectOption id={branch.id} key={branch.id} value={branch.id} disabled>
        <div className="horizontal-box">{branch.label}</div>
      </SelectOption>
    );
  };

  buildAllBranchFilterMenuItemDisplay = (branch: $TSFixMe): JSX.Element[] => {
    return branch.filters
      .map((filter: $TSFixMe) => {
        if (this.props.hideAllSessionsOption && filter.id === 'all') {
          return null;
        }
        const id = filter.id !== 'all' ? filter.id : 'all~' + filter.branchId;
        const filterClass =
          filter.id === this.state.selectedFilter.id && filter.branchId === this.state.selectedFilter.branchId
            ? 'body-2-text'
            : '';

        return (
          <SelectOption key={id} value={id}>
            <div className={filterClass}>{this.buildFilterMenuItemDisplay(filter, true)}</div>
          </SelectOption>
        );
      })
      .filter(Boolean);
  };

  buildFilterDisplayString(filter: $TSFixMe, showLabel: $TSFixMe) {
    const { isSessionsV2Enabled, branches } = this.props;
    const filterBranch = branches.find((branch) => branch.id === filter.branchId);
    const filterBranchLabel = filterBranch?.label || '';

    if (filter.id === 'ondemand') {
      return showLabel ? 'Self Paced' : `Self Paced (${filterBranchLabel})`;
    } else if (filter.id === 'all') {
      const sessionsDescriptorText = isSessionsV2Enabled ? 'cohorts' : 'sessions';
      return `All ${sessionsDescriptorText} for ${filterBranchLabel}`;
    } else {
      const startedAtDisplay = moment(filter.startedAt).format(dateFormat);
      const endedAtDisplay = moment(filter.endedAt).format(dateFormat);

      const dateDisplay = `${startedAtDisplay} - ${endedAtDisplay}`;
      return showLabel ? dateDisplay : dateDisplay + ` (${filterBranchLabel})`;
    }
  }

  buildFilterMenuItemDisplay = (filter: $TSFixMe, showLabel: $TSFixMe) => {
    if (filter.id === 'ondemand' || filter.id === 'all') {
      return (
        <div className="horizontal-box menu-item-box">
          {showLabel && <span>&nbsp;-&nbsp;</span>}
          {this.buildFilterDisplayString(filter, showLabel)}

          {
            // We only want the label if its for a session that has not yet ended.
            filter.id === 'ondemand' && showLabel && (
              <div className="menu-item-label caption-text color-secondary-text">{_t('Active')}</div>
            )
          }
        </div>
      );
    }

    // If it's not `ondemand` or `all` then it is a session

    // NOTE this calculated week could be different from the actual week. Getting the schedule for all sessions would
    // be expensive, so just make a best guess based on session deadlines.
    const session = this.props.allSessions.find((s) => s.id === filter.id);
    const earliestDeadline = _(session?.moduleDeadlines)
      .chain()
      .sortBy((b) => b.startedAt)
      .find((moduleDeadline) => moment(moduleDeadline.deadline).isAfter(moment()))
      .value();
    const weekNumber = earliestDeadline && moment(earliestDeadline).diff(moment(session?.startedAt), 'weeks');

    return (
      <div className="horizontal-box menu-item-box">
        {showLabel && <span>&nbsp;-&nbsp;</span>}
        {this.buildFilterDisplayString(filter, showLabel)}

        {showLabel &&
          // if findIndex returned -1 that means there isn't actually a moduleDeadline that is after now.
          weekNumber > 0 &&
          // We only want the label if its for a session that has not yet ended.
          moment(session?.endedAt).isAfter(moment()) && (
            <div className="menu-item-label caption-text color-secondary-text">
              <FormattedMessage message={_t('Week {weekNumber} assignment due')} weekNumber={weekNumber} />
            </div>
          )}
      </div>
    );
  };

  render() {
    if (!this.props.branches || this.props.branches.length === 0) {
      return null;
    }

    // if we have a requiredBranchId, only show the sessions for that branch
    const branches = this.props.limitSwitchingToCurrentBranch
      ? this.props.branches.filter((branch) => branch.id === this.props.currentBranchId)
      : this.props.branches;

    const styles = css({
      'ul > li.disabled > a:hover': {
        cursor: 'default',
        backgroundColor: 'var(--cds-color-neutral-background-primary)',
      },
      'ul > li.disabled > a:focus': {
        outline: 'none',
        backgroundColor: 'var(--cds-color-neutral-background-primary)',
      },
      '.menu-item-box': {
        overflow: 'hidden',
      },
      '.menu-item-label': {
        backgroundColor: this.props.theme.palette.blue[50],
        borderRadius: '2px',
        padding: this.props.theme.spacing(4, 12),
        border: `1px solid ${this.props.theme.palette.gray[300]}`,
        marginLeft: this.props.theme.spacing(12),
      },
    });

    const selectOptions: JSX.Element[] = [];
    for (const branch of branches) {
      // don't show the branch name if there is only a single branch
      const showBranchName = branches.length > 1;
      if (showBranchName) {
        selectOptions.push(this.buildBranchMenuItemDisplay(branch));
      }
      selectOptions.push(...this.buildAllBranchFilterMenuItemDisplay(branch));
    }

    return (
      <div className="rc-personalization_SessionFilterDropdown body-1-text" css={styles}>
        <div className="dropdown-section">
          <SelectField
            id={this.props.id}
            placeholder={this.buildFilterMenuItemDisplay(this.state.selectedFilter, false) as unknown as string}
            aria-label={_t('Select session')}
            onChange={(val) => this.handleDropdownChange(val.target.value)}
          >
            {selectOptions}
          </SelectField>
        </div>
      </div>
    );
  }
}

export default _.compose(
  connectToStores(['SessionFilterStore'], ({ SessionFilterStore }, props) => {
    return {
      branches: SessionFilterStore.branches,
      sessionsLoaded: SessionFilterStore.filtersLoaded,
      selectedFilter: SessionFilterStore.activeFilter,
      allSessions: SessionFilterStore.allSessions,
      currentBranchId: SessionFilterStore.currentBranchId,
    };
  }),
  waitFor(
    (props) => props.sessionsLoaded,
    () => <div className="padded-label">{_t('Loading...')}</div>
  )
)(withTheme(SessionFilterDropdown));
